import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLine } from '../aiAccess/shared';
import {
  looksLikeSerial,
  normalizeSerial,
  type EnrichmentClient,
  type ProductListItem,
  type ProductLookup,
  type ProductPage,
  type ProductStats,
  type WarrantyFilter,
} from './api';
import { ORIGINS, attentionBadge, oemName, productTitle, serialCount, shortDate, warrantyBadge } from './format';
import { Badge, Icon, MONO, buttonClass, chipClass } from './ui';

export const PRODUCTS_PAGE_SIZE = 25;
export const SEARCH_DELAY_MS = 300;

type Filter = 'all' | WarrantyFilter;

const TH = 'border-b border-[#E5E7EB] dark:border-[#30363d] bg-[#F1F2F5] dark:bg-[#161b22] px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-[.05em] text-[#5C6B85] dark:text-[#8b949e]';
const TD = 'border-b border-[#E5E7EB] dark:border-[#30363d] px-4 py-3.5 align-top text-sm text-[#0B1B3D] dark:text-[#e6edf3]';

/**
 * The stored products: a search over what is stored (it never asks the OEM), the warranty chips
 * and the OEM filter (counts from `stats`; an older service without them gets the search alone),
 * the list, and paging. A row opens the drawer (`onOpen`). A search that looks like a serial and
 * matches nothing stored offers "Look it up", which hands it to the lookup card (`onLookUp`).
 */
export function ProductsPanel({ client, stats, version, onOpen, onLookUp, searchRef }: {
  readonly client: EnrichmentClient | null;
  readonly stats: ProductStats | null;
  /** Bumped when something was stored: the list loads again. */
  readonly version: number;
  readonly onOpen: (serial: string, answer: ProductLookup | null) => void;
  readonly onLookUp: (serial: string) => void;
  readonly searchRef: RefObject<HTMLInputElement>;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [oem, setOem] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<{ key: string; data: ProductPage } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  // The search goes out once typing pauses, from the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setOffset(0);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const key = JSON.stringify([search, filter, oem, offset]);
  const fetchPage = useCallback(async () => {
    if (!client) return;
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setLoading(true);
    setError(null);
    try {
      const data = await client.products({
        q: search,
        limit: PRODUCTS_PAGE_SIZE,
        offset,
        ...(filter === 'all' ? {} : { warranty: filter }),
        ...(oem ? { oem } : {}),
      }, request.signal);
      if (!request.signal.aborted) setPage({ key: JSON.stringify([search, filter, oem, offset]), data });
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [client, search, filter, oem, offset, t]);

  const ready = Boolean(client);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  useEffect(() => {
    if (ready) void fetchRef.current();
  }, [ready, key]);
  const firstVersion = useRef(version);
  useEffect(() => {
    if (version !== firstVersion.current) void fetchRef.current();
  }, [version]);
  useEffect(() => () => requestRef.current?.abort(), []);

  const items = page?.data.items ?? [];
  const total = page?.data.total ?? 0;
  const unfiltered = filter === 'all' && !oem;
  // Nothing stored matches what could be a serial: offer to look it up.
  const notStored = page?.key === key && total === 0 && unfiltered && looksLikeSerial(search) ? normalizeSerial(search) : null;

  const units = stats?.units;
  const chips: Array<{ value: Filter; label: string; count: number | undefined }> = units ? [
    { value: 'all', label: t('All'), count: units.total },
    { value: 'active', label: t('Active'), count: units.active },
    { value: 'ending', label: t('Ending soon'), count: units.ending_soon },
    { value: 'expired', label: t('Expired'), count: units.expired },
    { value: 'attention', label: t('Not found'), count: stats?.attention?.total },
  ] : [];
  const oems = stats?.oems ?? [];
  const endingSoonDays = stats?.ending_soon_days ?? 90;
  const choose = (next: Filter) => {
    setFilter(next);
    setOffset(0);
  };

  return (
    <section aria-label={t('Stored products')} className="overflow-hidden rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[#E5E7EB] dark:border-[#30363d] px-4 py-3.5">
        <div className="relative min-w-[240px] flex-[1_1_320px]">
          <span className="pointer-events-none absolute left-3.5 top-3 text-[#5C6B85] dark:text-[#8b949e]"><Icon name="search" /></span>
          <input
            ref={searchRef}
            type="search"
            aria-label={t('Search stored serials')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Search stored serials, products or models')}
            className="box-border h-10 w-full rounded-full border border-[#E5E7EB] bg-[#F1F2F5] pl-10 pr-4 text-[13px] text-[#0B1B3D] outline-none placeholder:text-[#5C6B85] focus-visible:border-[#0B1B3D] dark:border-[#30363d] dark:bg-[#161b22] dark:text-[#e6edf3] dark:placeholder:text-[#8b949e] dark:focus-visible:border-[#e6edf3]"
          />
        </div>
        {chips.length > 0 && (
          <div role="group" aria-label={t('Warranty filter')} className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <button key={chip.value} type="button" aria-pressed={filter === chip.value} className={chipClass(filter === chip.value)} onClick={() => choose(chip.value)}>
                {chip.label}
                {chip.count !== undefined && <>{' '}<span>{chip.count.toLocaleString()}</span></>}
              </button>
            ))}
          </div>
        )}
        {oems.length > 0 && (
          <span className="relative inline-flex">
            <select
              aria-label={t('OEM filter')}
              value={oem}
              onChange={(event) => {
                setOem(event.target.value);
                setOffset(0);
              }}
              className={`${buttonClass({ small: true })} appearance-none pr-8`}
            >
              <option value="">{t('All OEMs')}</option>
              {oems.map((entry) => <option key={entry.code} value={entry.code}>{entry.name || oemName(entry.code)}</option>)}
            </select>
            <span className="pointer-events-none absolute right-3 top-[9px] text-[#0B1B3D] dark:text-[#e6edf3]"><Icon name="chevronDown" size={14} /></span>
          </span>
        )}
      </div>

      {error && <div className="px-4 pt-3"><ErrorLine message={error} /></div>}

      {notStored && !loading ? (
        <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
          <p className="m-0 text-sm text-[#5C6B85] dark:text-[#8b949e]">
            <span className={`${MONO} font-medium text-[#0B1B3D] dark:text-[#e6edf3]`}>{notStored}</span> {t('isn’t stored yet.')}
          </p>
          <button type="button" className={buttonClass({ small: true })} onClick={() => onLookUp(notStored)}>
            <Icon name="search" size={14} />{t('Look it up')}
          </button>
        </div>
      ) : (
        <>
          {page && total === 0 && !loading && (
            <p className="m-0 px-4 py-8 text-center text-sm text-[#5C6B85] dark:text-[#8b949e]">
              {emptyText(t, search, unfiltered)}
            </p>
          )}
          {!page && loading && <p className="m-0 px-4 py-8 text-center text-sm text-[#5C6B85] dark:text-[#8b949e]">{t('Loading…')}</p>}
          {items.length > 0 && (
            <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
              <table aria-label={t('Stored products')} className="w-full min-w-[860px] border-collapse">
                <thead>
                  <tr>
                    <th className={TH}>{t('Serial')}</th>
                    <th className={TH}>{t('Product')}</th>
                    <th className={TH}>{t('OEM')}</th>
                    <th className={TH}>{t('Warranty')}</th>
                    <th className={`${TH} !text-right`}>{t('Coverages')}</th>
                    <th className={TH}>{t('Last checked')}</th>
                    <th className={TH}><span className="sr-only">{t('Open')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <ProductRow key={item.serial_number} item={item} endingSoonDays={endingSoonDays} onOpen={() => onOpen(item.serial_number, null)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {page && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 text-[13px] text-[#5C6B85] dark:text-[#8b949e]">
              <span>
                {t('{{from}}–{{to}} of {{serials}}', {
                  from: offset + 1,
                  to: offset + items.length,
                  serials: page.data.total_capped ? t('{{n}}+ serials', { n: total.toLocaleString() }) : serialCount(total, t),
                })}
              </span>
              <div className="flex gap-2">
                <button type="button" className={buttonClass({ small: true })} onClick={() => setOffset(Math.max(0, offset - PRODUCTS_PAGE_SIZE))} disabled={loading || offset === 0}>
                  {t('Previous')}
                </button>
                <button
                  type="button"
                  className={buttonClass({ small: true })}
                  onClick={() => setOffset(offset + PRODUCTS_PAGE_SIZE)}
                  disabled={loading || items.length < PRODUCTS_PAGE_SIZE || (!page.data.total_capped && offset + items.length >= total)}
                >
                  {t('Next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function emptyText(t: (key: string, values?: Record<string, unknown>) => string, search: string, unfiltered: boolean): string {
  if (!unfiltered) return search ? t('No stored serials match “{{search}}” with these filters.', { search }) : t('No stored serials match these filters.');
  if (search) return t('No stored serials match “{{search}}”.', { search });
  return t('No serials are stored yet. Look a serial up above, or upload a list.');
}

/** "2 of 4 active", or the total alone from a service that does not count the active ones. */
export function coveragesText(t: (key: string, values?: Record<string, unknown>) => string, warranty: ProductListItem['warranty'] | null | undefined): string {
  const total = warranty?.coverage_count ?? 0;
  const active = warranty?.active_count;
  if (typeof active !== 'number') return total.toLocaleString();
  return t('{{active}} of {{total}} active', { active: active.toLocaleString(), total: total.toLocaleString() });
}

function ProductRow({ item, endingSoonDays, onOpen }: {
  readonly item: ProductListItem;
  readonly endingSoonDays: number;
  readonly onOpen: () => void;
}) {
  const { t } = useTranslation();
  const check = item.check;
  const stored = item.found !== false;
  const warranty = item.warranty;
  const oem = oemName(item.oem) ?? oemName(check?.detection?.oem ?? null);
  const badge = stored ? warrantyBadge(warranty?.status) : attentionBadge(check?.status);
  let note: string | null = null;
  let noteSoon = false;
  if (stored && warranty?.status === 'active' && warranty.end_date) {
    const days = warranty.days_remaining;
    note = days === null || days === undefined
      ? t('until {{date}}', { date: warranty.end_date })
      : t('until {{date}} · {{days}} days', { date: warranty.end_date, days: days.toLocaleString() });
    noteSoon = days !== null && days !== undefined && days <= endingSoonDays;
  } else if (stored && warranty?.status === 'expired' && warranty.end_date) {
    note = t('ended {{date}}', { date: warranty.end_date });
  } else if (stored && warranty?.status === 'not_started' && warranty.start_date) {
    note = t('starts {{date}}', { date: warranty.start_date });
  } else if (!stored && check?.status === 'needs_more_info' && check.missing_fields?.length) {
    note = t('needs {{fields}}', { fields: check.missing_fields.join(', ').replace(/_/g, ' ') });
  }

  return (
    <tr onClick={onOpen} className="cursor-pointer hover:bg-[#F7F8FA] dark:hover:bg-[#161b22]">
      <td className={TD}><span className={`${MONO} text-[13px] font-medium`}>{item.serial_number}</span></td>
      <td className={TD}>
        {stored ? (
          <>
            <div className="font-semibold">{productTitle(item.product) ?? '—'}</div>
            {item.product?.product_number && <div className={`${MONO} mt-0.5 text-xs text-[#5C6B85] dark:text-[#8b949e]`}>{item.product.product_number}</div>}
          </>
        ) : (
          <span className="text-[#5C6B85] dark:text-[#8b949e]">{t('No product stored')}</span>
        )}
      </td>
      <td className={TD}>{oem ?? '—'}</td>
      <td className={TD}>
        <Badge tone={badge.tone}>{t(badge.label)}</Badge>
        {note && <div className={`mt-1.5 text-xs ${noteSoon ? 'text-[#92400E] dark:text-[#FBBF24]' : 'text-[#5C6B85] dark:text-[#8b949e]'}`}>{note}</div>}
      </td>
      <td className={`${TD} text-right`}>{stored ? coveragesText(t, warranty) : '—'}</td>
      <td className={`${TD} text-[#434A59] dark:text-[#c9d1d9]`}>
        {shortDate(check?.checked_at)}
        {check?.origin && <div className="text-xs text-[#5C6B85] dark:text-[#8b949e]">{t(ORIGINS[check.origin] ?? check.origin)}</div>}
      </td>
      <td className={`${TD} text-right`}>
        <button
          type="button"
          aria-label={t('Open {{serial}}', { serial: item.serial_number })}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          className="cursor-pointer border-0 bg-transparent p-0 text-[#5C6B85] dark:text-[#8b949e] hover:text-[#0B1B3D] dark:hover:text-[#e6edf3]"
        >
          <Icon name="chevronRight" size={18} />
        </button>
      </td>
    </tr>
  );
}
